import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router";
import { useDidMount } from "../../coraWebComponents/utils/hooks";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import "./Ziadosti.scss";

// proptypes
import ziadostPropTypes from "../../propTypes/ziadostPropTypes";

// components
import ZiadostDetail from "./ZiadostDetail";
import Grid from '../../coraWebComponents/dataGrid/DataGrid';
import flags from '../../coraWebComponents/dataGrid/flags';
import Tab from "../../coraWebComponents/Tab";
import Tabs from "../../coraWebComponents/TabsWrapper";

// coraWebComponents
import Button from "../../coraWebComponents/buttons/Button";

// Constants
import Routes from "../../constants/Routes";
import * as storage from '../../coraWebComponents/utils/storage';
import { showMsg } from '../../coraWebComponents/utils/logger';

// actions
import * as appHeaderActions from "../../actions/appHeaderActions";
import * as filesActions from "../../actions/filesActions";
import * as zpCDovodActions from "../../actions/ciselniky/zpCDovodActions";
import * as zpCPrioritaActions from "../../actions/ciselniky/zpCPrioritaActions";
import * as zpCStavZiadostiActions from "../../actions/ciselniky/zpCStavZiadostiActions";
import * as zpCTypPraceActions from "../../actions/ciselniky/zpCTypPraceActions";
import * as ziadostActions from "../../actions/ziadostActions";

import * as ziadostUtils from "../../utils/ziadostUtils";
import _ from "lodash";

const VsetkyZiadosti = (props) => {
  const { isLoading, total, listZiadosti } = props;

  const [selected, setSelected] = useState([]);
  const filter = useRef({ filters: [] });
  const sort = useRef([]);
  const page = useRef({ skip: 0, take: 10 });
  const setFilter = (newFilter) => { filter.current = newFilter };
  const setSort = (newSort) => { sort.current = newSort };
  const setPage = (newPage) => { page.current = newPage };

  const [data, setData] = useState(listZiadosti);

  const navigate = useNavigate();

  const expandChange = useCallback((event) => {
    const id = parseInt(_.keys(event.detailExpand)[0]);

    setData(prevData =>
      prevData.map(x =>
        x.I_AG_ZIADOST === id
          ? { ...x, expanded: !x.expanded }
          : x
      )
    );
  }, []);

  useEffect(() => {
    setData(listZiadosti);
  }, [listZiadosti]);


  // GRID PROPS
  const handleChange = useCallback((filter, sort, page) => {
    setFilter(filter);
    setSort(sort);
    setPage(page);
  }, [])

  const handleOnGetList = useCallback(async () => {
    await props.getListZiadosti(filter.current, sort.current, page.current);

    setSelected(prevSelected =>
      props.listZiadosti.filter(item =>
        prevSelected.find(
          x => x.I_AG_ZIADOST === item.I_AG_ZIADOST
        )
      )
    );
  }, [props.getListZiadosti, props.listZiadosti]);

  const handleOnGet = useCallback((item) => {
    const ziadost = item?.Data ?? null;
    props.changeZiadost(ziadost);
  }, [props.changeZiadost]);

  const genSchema = () => {
    return [
      {
        field: "KOD_ZIADOSTI",
        title: "Kód",
        width: "auto",
        filterable: true,
        filter: "text",
        filterTitle: "Kód"
      },
      {
        field: "ADRESA_STAVBA",
        title: "Adresa",
        width: "auto",
        filterable: true,
        filter: "text",
        filterTitle: "Adresa"
      },
      {
        field: "ZIADATEL",
        title: "Žiadateľ",
        width: "auto",
        filterable: true,
        filter: "text",
        filterTitle: "Žiadateľ"
      },
      {
        field: "N_STAV_ZIAD",
        title: "Stav",
        width: "140px",
        filterable: true,
        filter: "text",
        filterTitle: "Stav"
      },
      {
        field: "D_POD",
        title: "Vytvorené",
        width: "auto",
        format: "{0:dd.MM.yyyy}",
        filterable: true,
        filter: "datetimeinput",
        operator: "datetimeinput",
        type: "datetimeinput",
        filterTitle: "Vytvorené"
      },
      {
        field: "N_UZ_VYBAVUJE",
        title: "Vybavuje",
        width: "auto",
        filterable: true,
        filter: "text",
        filterTitle: "Vybavuje"
      },
      {
        field: "Priorita.SKR",
        title: "Priorita",
        width: "auto",
        filterable: true,
        filter: "text",
        filterTitle: "Priorita"
      },
      {
        field: "D_VYBAVENIA",
        title: "Vybavené",
        width: "auto",
        format: "{0:dd.MM.yyyy}",
        filterable: true,
        filter: "datetimeinput",
        operator: "datetimeinput",
        type: "datetimeinput",
        filterTitle: "Vybavené"
      },
    ];
  };

  const schema = useMemo(() => genSchema(), []);

  const fixedFilter = useMemo(() => ({ filters: [] }), []);
  const noop = useCallback(() => { }, []);
  const emptyArray = useMemo(() => [], []);

  // BUTTON PROPS
  const handleOnBtnVyjadClick = (e) => {
    if (selected.length !== 1) {
      props.showMsg("Je potrebné označiť práve jeden záznam.");
      return;
    }

    navigate(Routes.VYTVORIT_VYJADRENIE, { state: { skipBlocker: true, from: Routes.VSETKY_ZIADOSTI } });
  };

  const handleOnBtnRezClick = async (e) => {
    const otvorene = selected.filter(item => item.I_STAV_ZIAD === 1);
    await Promise.all(otvorene.map(async (item) => {
      await ziadostUtils.vybavuje(item.I_AG_ZIADOST);
      return item;
    }));

    handleOnGetList();
  };

  const handleOnBtnZrusRezClick = async (e) => {
    const user = storage.getUser();
    const mojeRezervovane = selected.filter(item => item.I_STAV_ZIAD === 4 && item.I_UZ_VYBAVUJE === user.Uzivatel.I_UZ);
    await Promise.all(mojeRezervovane.map(async (item) => {
      await ziadostUtils.zrusRezervaciu(item.I_AG_ZIADOST);
      return item;
    }));

    handleOnGetList();
  };

  useDidMount(async () => {
    const {
      showAppHeader,
      changeHeaderTitle,
      listDovod,
      listPriorita,
      listStavZiadosti,
      listTypPrace,
      getListZP_C_DOVOD,
      getListZP_C_PRIORITA,
      getListZP_C_STAV_ZIADOSTI,
      getListZP_C_TYP_PRACE,
    } = props;

    showAppHeader(false);
    changeHeaderTitle("Všetky žiadosti");
    // get ciselniky data
    if (listDovod.length === 0) await getListZP_C_DOVOD();
    if (listPriorita.length === 0) await getListZP_C_PRIORITA();
    if (listStavZiadosti.length === 0) await getListZP_C_STAV_ZIADOSTI();
    if (listTypPrace.length === 0) await getListZP_C_TYP_PRACE();
  })

  useEffect(() => {
    return () => {
      props.showAppHeader(false);
    }
  }, []); // eslint-disable-line

  const getActions = useCallback(() => {
    const user = storage.getUser();
    const otvorene = selected.filter(item => item.I_STAV_ZIAD === 1);
    const mojeRezervovane = selected.filter(item => item.I_STAV_ZIAD === 4 && item.I_UZ_VYBAVUJE === user.Uzivatel.I_UZ);
    const btnRezDisabled = otvorene.length === 0;
    const btnZrusRezDisabled = mojeRezervovane.length === 0;
    const btnVyjDisabled = selected.length === 0;

    return (
      <div className="btnRezVyjadrenie">
        <Button
          primary={true}
          onClick={handleOnBtnRezClick}
          disabled={btnRezDisabled}
          label="Rezervovať"
        />
        <Button
          primary={true}
          onClick={handleOnBtnZrusRezClick}
          disabled={btnZrusRezDisabled}
          label="Zrušiť rezerváciu"
        />
        <Button
          primary={true}
          onClick={handleOnBtnVyjadClick}
          disabled={btnVyjDisabled}
          label="Vytvoriť vyjadrenie"
        />
      </div>
    )
  }, [selected]);

  const cellRender = useCallback((tdElement, cellProps) => {
    const { field, dataItem } = cellProps;
    var tdProps = { ...tdElement.props };
    var children = tdElement.props.children;

    if (field === "N_STAV_ZIAD") {
      tdProps.className += " stav";
      const className = (dataItem["N_STAV_ZIAD"] === "Uzavreté" ? " green" :
        (dataItem["N_STAV_ZIAD"] === "Zamietnuté" ? " red" :
          (dataItem["N_STAV_ZIAD"] === "Rezervované" ? " orange" : " yellow")));
      const iconClassName = (dataItem["N_STAV_ZIAD"] === "Uzavreté" ? "k-icon k-font-icon k-i-check" :
        (dataItem["N_STAV_ZIAD"] === "Zamietnuté" ? "k-icon k-font-icon k-i-close" :
          (dataItem["N_STAV_ZIAD"] === "Rezervované" ? "k-icon k-font-icon k-i-lock" : "k-icon k-font-icon k-i-folder-open")));
      children = (<div className={className}><span>{children}</span><span className={iconClassName}></span></div>);
    }

    return React.cloneElement(tdElement, { ...tdProps }, children);
  }, []);


  const handleGridSelect = useCallback((selected) => {
    setSelected(selected);
  }, []);

  const isDesktop = useMemo(() => {
    return window.matchMedia('only screen and (min-width: 768px)').matches
  }, []);

  const initSort = useMemo(() => [
    { field: "D_POD", dir: "desc" }
  ], []);

  return (
    <div className="row m-0">
      <div className="col p-0">
        <div className="grid-info-row"><div className="grid-info-row-name">Všetky žiadosti</div><div className="grid-info-row-filters"></div></div>
        <Tabs>
          <Tab title="Zoznam">
            <Grid
              className={(isDesktop ? "" : " mobile") + " ziadosti"}
              total={total}
              data={data}
              isLoading={isLoading}
              onChange={handleChange}
              onGet={handleOnGet}
              onGetList={handleOnGetList}
              onExpandChange={expandChange}
              selected={selected}
              onSelect={handleGridSelect}
              primaryField="I_AG_ZIADOST"
              schema={schema}
              detailSchema={emptyArray}
              detail={ZiadostDetail}
              cellRender={cellRender}
              toolbarActions={getActions}
              allColumns
              multiple
              printable
              cellSelect
              fixedFilter={fixedFilter}
              onChangeField={noop}
              onDelete={noop}
              onCancel={noop}
              updateSort={noop}
              initSort={initSort}
              filter={filter.current}
              sort={sort.current}
              page={page.current}
              hidden={flags.ADD | flags.COPY | flags.DELETE | flags.CANCEL | flags.RESET |
                flags.EDIT | flags.COLUMN_MENU | flags.COLUMN_SELECTED | flags.EDITALL | flags.GRID_CELL_EDIT | flags.USER_FILTER | flags.DETAIL}
              hiddenBack
              userFilterList={emptyArray}
              userFilterExtended={emptyArray}
              showMsg={props.showMsg}
            />
          </Tab>
        </Tabs>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    // list ziadosti
    isLoading: state.allListZiadost.isLoading || state.files.isLoading,
    listZiadosti: state.allListZiadost.data,
    total: state.allListZiadost.total,

    // ciselniky
    listDovod: state.zpCDovod.list,
    listPriorita: state.zpCPriorita.list,
    listStavZiadosti: state.zpCStavZiadosti.list,
    listTypPrace: state.zpCTypPrace.list,
    //oznacena ziadost
    ziadost: state.ziadost.data,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getListZP_C_DOVOD: async () =>
    await dispatch(zpCDovodActions.getZP_C_DOVOD()),
  getListZP_C_PRIORITA: async () =>
    await dispatch(zpCPrioritaActions.getZP_C_PRIORITA()),
  getListZP_C_STAV_ZIADOSTI: async () =>
    await dispatch(zpCStavZiadostiActions.getZP_C_STAV_ZIADOSTI()),
  getListZP_C_TYP_PRACE: async () =>
    await dispatch(zpCTypPraceActions.getZP_C_TYP_PRACE()),
  getListZiadosti: (filter, sort, page) =>
    dispatch(ziadostActions.getAllListZiadost(filter, sort, page)),
  downloadPriloha: (I_AG_ZIAD_BIN, FILE_NAME, L_VYJADRENIE) =>
    dispatch(filesActions.downloadPriloha(I_AG_ZIAD_BIN, FILE_NAME, L_VYJADRENIE)),
  changeZiadost: (ziadost) => dispatch(ziadostActions.changeZiadost(ziadost)),
  changeHeaderTitle: (title) => dispatch(appHeaderActions.changeHeaderTitle(title)),
  showAppHeader: (show) => dispatch(appHeaderActions.showAppHeader(show)),
  showMsg: (msg, msgType = 'ok', isDialog = false, title) => dispatch(showMsg(msg, msgType, isDialog, title))
});

VsetkyZiadosti.propTypes = {
  changeHeaderTitle: PropTypes.func,
  changeZiadost: PropTypes.func,
  downloadPriloha: PropTypes.func,
  getListZP_C_DOVOD: PropTypes.func,
  getListZP_C_PRIORITA: PropTypes.func,
  getListZP_C_STAV_ZIADOSTI: PropTypes.func,
  getListZP_C_TYP_PRACE: PropTypes.func,
  getListZiadosti: PropTypes.func,
  isLoading: PropTypes.bool,
  listZiadosti: PropTypes.arrayOf(ziadostPropTypes.ziadost),
  showAppHeader: PropTypes.func,
  total: PropTypes.number,
};

export default connect(mapStateToProps, mapDispatchToProps)(VsetkyZiadosti);
